
//	Captures utm_* campaign params from the landing URL so they can be attached to the
//	`create-user` request once the user finishes signup (which may be several page loads
//	and steps later -- see signup-panel.tsx). Storage is last-touch: whichever utm_* set
//	was seen most recently (that had at least one non-empty value) wins, and it survives
//	page reloads within the tab (sessionStorage), but not across tabs or browser restarts.
//	Every entry point is wrapped in try/catch: sessionStorage can throw (eg. private
//	browsing, blocked storage), and losing attribution must never break the page.

const STORAGE_KEY = 'signupAttribution'

const UTM_PARAM_NAMES = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const

type UtmParamName = typeof UTM_PARAM_NAMES[number]

//	sanity cap only, deliberately looser than the server's 100-char limit (see
//	backend/lambda/create-user/src/signup_attribution.ts) -- it just keeps a malformed URL
//	from filling sessionStorage with megabytes of junk. The server is the real boundary: it
//	enforces length and charset, and is the one whose rejection actually matters.
const MAX_VALUE_LENGTH = 500

export type SignupAttribution = Partial<Record<UtmParamName, string>> & {
	capturedAt: number
}

//	Reads utm_* params from the current URL. If none are present, does nothing (an earlier
//	capture, if any, is left in place). If at least one is present, it replaces whatever was
//	stored before -- a pure side effect, safe to call unconditionally on every page load.
export function captureSignupAttribution(): void {
	try {
		const params = new URLSearchParams(window.location.search)
		const attribution: Partial<Record<UtmParamName, string>> = {}
		let hasAny = false
		for (const name of UTM_PARAM_NAMES) {
			const rawValue = params.get(name)
			if (!rawValue)
				continue
			const value = rawValue.trim().slice(0, MAX_VALUE_LENGTH)
			if (value.length === 0)
				continue
			attribution[name] = value
			hasAny = true
		}
		if (!hasAny)
			return

		const toStore: SignupAttribution = { ...attribution, capturedAt: Date.now() }
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
	} catch (error) {
		console.error('captureSignupAttribution failed:', error)
	}
}

export function readSignupAttribution(): SignupAttribution | undefined {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		if (!raw)
			return undefined
		return JSON.parse(raw) as SignupAttribution
	} catch (error) {
		console.error('readSignupAttribution failed:', error)
		return undefined
	}
}

export function clearSignupAttribution(): void {
	try {
		sessionStorage.removeItem(STORAGE_KEY)
	} catch (error) {
		console.error('clearSignupAttribution failed:', error)
	}
}
